import React, {ComponentProps, forwardRef, useState} from 'react';
import styled, {css, useTheme} from 'styled-components';

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  position: relative;
  grid-area: ${(props) => (props.style?.gridArea ? props.style?.gridArea : '')};
`;

const InputShell = styled.div<{
  $variant: 'default' | 'brand';
  $focused?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 46px;
  transition: border-color ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    box-shadow ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out},
    background ${(p) => p.theme.motion.duration.base}
      ${(p) => p.theme.motion.easing.out};

  ${(p) =>
    p.$variant === 'brand'
      ? css`
          border: none;
          border-radius: 12px;
          background: ${p.theme.gradients.brand};
          box-shadow: ${p.theme.elevation.brand};
          padding: 0 12px;

          &:hover {
            background: ${p.theme.gradients.brandHover};
          }
        `
      : css`
          border: ${p.$focused ? '1.5px' : '1px'} solid
            ${p.$focused
              ? p.theme.colors.violet600
              : p.theme.colors.borderStrong};
          border-radius: 999px;
          background: ${p.theme.colors.surfaceSunken};
          box-shadow: ${p.theme.elevation.xs};
          padding-left: 44px;
          padding-right: 12px;
        `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Input = styled.input<{
  $variant: 'default' | 'brand';
  $hasClear?: boolean;
}>`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  -webkit-appearance: none;
  appearance: none;
  font-size: 0.875rem;
  line-height: 1.3125;
  font-weight: 500;
  outline: none;

  ${(p) =>
    p.$variant === 'brand'
      ? css`
          padding: 0 8px;
          text-align: center;
          color: ${p.theme.colors.neutral100};
          background: transparent;
          background-color: transparent;

          &::placeholder {
            color: rgba(255, 255, 255, 0.85);
            font-weight: 400;
            text-align: center;
          }

          &:-webkit-autofill,
          &:-webkit-autofill:hover,
          &:-webkit-autofill:focus {
            -webkit-text-fill-color: ${p.theme.colors.neutral100};
            caret-color: ${p.theme.colors.neutral100};
            transition: background-color 9999s ease-in-out 0s;
            box-shadow: 0 0 0 1000px transparent inset;
          }
        `
      : css`
          padding: 0;
          padding-right: ${p.$hasClear ? '28px' : '0'};
          text-align: left;
          color: ${p.theme.colors.textPrimary};
          /* Explicit sunken fill — transparent alone still paints UA white */
          background: ${p.theme.colors.surfaceSunken};
          background-color: ${p.theme.colors.surfaceSunken};

          &::placeholder {
            color: ${p.theme.colors.textMuted};
            font-weight: 400;
          }

          &:-webkit-autofill,
          &:-webkit-autofill:hover,
          &:-webkit-autofill:focus {
            -webkit-text-fill-color: ${p.theme.colors.textPrimary};
            caret-color: ${p.theme.colors.textPrimary};
            transition: background-color 9999s ease-in-out 0s;
            box-shadow: 0 0 0 1000px ${p.theme.colors.surfaceSunken} inset;
          }
        `}
`;

const SideIcon = styled.span<{
  $variant: 'default' | 'brand';
  $side: 'left' | 'right';
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  pointer-events: none;
  color: ${(p) =>
    p.$variant === 'brand'
      ? p.theme.colors.neutral100
      : p.theme.colors.textMuted};

  ${(p) =>
    p.$side === 'left'
      ? css`
          left: ${p.$variant === 'brand' ? '12px' : '14px'};
        `
      : css`
          right: 12px;
        `}
`;

const ClearButton = styled.button<{$variant: 'default' | 'brand'}>`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(p) =>
    p.$variant === 'brand'
      ? p.theme.colors.neutral100
      : p.theme.colors.textMuted};

  &:hover {
    opacity: 0.85;
  }
`;

const SearchGlyph = ({color}: {color: string}) => (
  <svg
    width="18"
    height="17"
    viewBox="0 0 18 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M16.3918 16.1678C16.2776 16.1678 16.1733 16.1251 16.09 16.0442L11.2322 11.092L10.9351 10.7891L10.5879 11.033C9.49921 11.7976 8.17152 12.2187 6.84941 12.2187C5.21834 12.2187 3.74553 11.5831 2.70229 10.4288C1.70272 9.32248 1.15744 7.88394 1.16696 6.37826C1.17648 4.87256 1.73991 3.44148 2.75346 2.34863C3.84272 1.17378 5.37639 0.5 6.96127 0.5C8.29687 0.5 9.60154 0.97737 10.635 1.84416C11.7695 2.79644 12.4662 4.05485 12.6495 5.48325C12.8298 6.88841 12.5112 8.37294 11.728 9.77636L11.546 10.1025L11.8071 10.3695L16.7093 15.3841C16.8767 15.5631 16.8735 15.8612 16.7016 16.0366C16.6175 16.1223 16.5102 16.1678 16.3918 16.1678ZM6.95922 1.50038C4.27108 1.50038 2.08412 3.68734 2.08412 6.37547C2.08412 9.06361 4.27108 11.2506 6.95922 11.2506C9.64507 11.2481 11.8318 9.06132 11.8343 6.37593C11.8343 3.68734 9.64735 1.50038 6.95922 1.50038Z"
      fill={color}
    />
  </svg>
);

const CloseGlyph = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

interface FilterInputProps extends Omit<ComponentProps<'input'>, 'type'> {
  placeHolder?: string;
  type?: string;
  isShadow?: boolean;
  variant?: 'default' | 'brand';
}

export const CustomSearchBar = forwardRef<HTMLInputElement, FilterInputProps>(
  (
    {
      placeHolder,
      type,
      isShadow,
      variant = 'default',
      value,
      onChange,
      onFocus,
      onBlur,
      style,
      className,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const [focused, setFocused] = useState(false);
    const hasValue = Boolean(value && String(value).length > 0);
    const iconColor = focused
      ? theme.colors.violet600
      : theme.colors.textMuted;

    const handleClear = () => {
      if (!onChange) {
        return;
      }
      const target = {value: ''} as HTMLInputElement;
      onChange({
        target,
        currentTarget: target,
      } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <Container style={style} className={className}>
        <InputShell
          $variant={variant}
          $focused={focused}
          style={isShadow ? {boxShadow: theme.shadow.shadow1} : undefined}
        >
          {variant === 'default' ? (
            <SideIcon $variant={variant} $side="left">
              <SearchGlyph color={iconColor} />
            </SideIcon>
          ) : (
            <SideIcon $variant={variant} $side="left" />
          )}

          <Input
            {...props}
            $variant={variant}
            $hasClear={hasValue}
            value={value}
            onChange={onChange}
            required
            placeholder={placeHolder}
            type={type || 'text'}
            ref={ref}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
          />

          {hasValue ? (
            <ClearButton
              type="button"
              $variant={variant}
              aria-label="Clear"
              onClick={handleClear}
            >
              <CloseGlyph />
            </ClearButton>
          ) : variant === 'brand' ? (
            <SideIcon $variant={variant} $side="right">
              <SearchGlyph color={theme.colors.neutral100} />
            </SideIcon>
          ) : null}
        </InputShell>
      </Container>
    );
  }
);

CustomSearchBar.displayName = 'CustomSearchBar';
